import { useState } from "react";
import { Package, Search, Plus, ExternalLink, Loader2, CheckCircle2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import axios from "axios";
import { API, useAuth } from "@/App";
import { toast } from "sonner";

const TopTexCatalogPage = () => {
  const { token } = useAuth();
  const [searchRef, setSearchRef] = useState("");
  const [searchBrand, setSearchBrand] = useState("");
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [importing, setImporting] = useState({});
  const [imported, setImported] = useState({});
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState("");

  useState(() => {
    axios.get(`${API}/categories`).then(r => setCategories(r.data)).catch(() => {});
  }, []);

  const handleSearch = async () => {
    if (!searchRef && !searchBrand) {
      toast.error("Entrez une référence ou une marque");
      return;
    }
    setLoading(true);
    try {
      const params = {};
      if (searchRef) params.catalog_reference = searchRef;
      if (searchBrand) params.brand = searchBrand;
      params.page_size = 50;
      const res = await axios.get(`${API}/toptex/search`, { params });
      setResults(res.data.products || []);
      if (res.data.count === 0) toast.info("Aucun produit trouvé");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur de recherche");
    }
    setLoading(false);
  };

  const handleImport = async (product) => {
    const ref = product.catalog_reference;
    setImporting(prev => ({ ...prev, [ref]: true }));
    try {
      const payload = {
        name: product.name,
        description: product.description + (product.composition ? ` | Composition: ${product.composition}` : ""),
        price: 0,
        category_id: selectedCategory || "",
        image_url: product.image_url || "",
        images: Object.values(product.color_images || {}),
        supplier: product.brand || "TopTex",
        customizable: true,
        min_quantity: 1,
        stock: 100,
        sku: ref,
        tags: [product.brand, product.family, product.collection].filter(Boolean),
        pricing_tiers: [],
        published: false,
        colors: product.colors || [],
        sizes: product.sizes || [],
        color_images: product.color_images || {},
      };
      await axios.post(`${API}/products`, payload, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setImported(prev => ({ ...prev, [ref]: true }));
      toast.success(`${product.name} importé !`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur d'import");
    }
    setImporting(prev => ({ ...prev, [ref]: false }));
  };

  return (
    <div className="space-y-6" data-testid="toptex-catalog">
      <h1 className="text-3xl font-bold text-[#1D3557]" style={{ fontFamily: "Fraunces, serif" }}>
        Catalogue TopTex
      </h1>

      <Card className="border-0 shadow-sm">
        <CardContent className="p-6">
          <div className="flex flex-wrap gap-4 items-end">
            <div className="flex-1 min-w-[200px]">
              <label className="text-sm font-medium text-stone-600 mb-1 block">Référence catalogue</label>
              <Input
                value={searchRef}
                onChange={(e) => setSearchRef(e.target.value)}
                placeholder="Ex: K740, GI18000, NS300..."
                onKeyDown={(e) => e.key === "Enter" && handleSearch()}
                data-testid="toptex-search-ref"
              />
            </div>
            <div className="flex-1 min-w-[200px]">
              <label className="text-sm font-medium text-stone-600 mb-1 block">Marque</label>
              <Input
                value={searchBrand}
                onChange={(e) => setSearchBrand(e.target.value)}
                placeholder="Ex: Kariban, Gildan, Native Spirit..."
                onKeyDown={(e) => e.key === "Enter" && handleSearch()}
                data-testid="toptex-search-brand"
              />
            </div>
            <div className="min-w-[200px]">
              <label className="text-sm font-medium text-stone-600 mb-1 block">Catégorie d'import</label>
              <select
                value={selectedCategory}
                onChange={(e) => setSelectedCategory(e.target.value)}
                className="w-full h-10 px-3 rounded-md border border-stone-200 text-sm"
              >
                <option value="">Sans catégorie</option>
                {categories.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
            </div>
            <Button onClick={handleSearch} disabled={loading} className="bg-[#1D3557] hover:bg-[#152a47] rounded-full h-10 px-6" data-testid="toptex-search-btn">
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4 mr-2" />}
              Rechercher
            </Button>
          </div>
        </CardContent>
      </Card>

      {results.length > 0 && (
        <p className="text-sm text-stone-500">{results.length} produit(s) trouvé(s)</p>
      )}

      <div className="grid gap-4">
        {results.map((product) => (
          <Card key={product.catalog_reference} className="border-0 shadow-sm overflow-hidden" data-testid={`toptex-product-${product.catalog_reference}`}>
            <CardContent className="p-0">
              <div className="flex flex-col md:flex-row">
                {/* Image */}
                <div className="w-full md:w-48 h-48 bg-stone-100 flex-shrink-0">
                  {product.image_url ? (
                    <img src={product.image_url} alt={product.name} className="w-full h-full object-contain p-2" />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center">
                      <Package className="w-12 h-12 text-stone-300" />
                    </div>
                  )}
                </div>

                {/* Info */}
                <div className="flex-1 p-5 space-y-3">
                  <div className="flex items-start justify-between gap-4">
                    <div>
                      <h3 className="font-semibold text-[#1D3557] text-lg">{product.name}</h3>
                      <p className="text-sm text-stone-500">
                        {product.brand} · Réf: {product.catalog_reference}
                        {product.collection && ` · ${product.collection}`}
                      </p>
                    </div>
                    {imported[product.catalog_reference] ? (
                      <Badge className="bg-green-100 text-green-700 gap-1">
                        <CheckCircle2 className="w-3 h-3" /> Importé
                      </Badge>
                    ) : (
                      <Button
                        onClick={() => handleImport(product)}
                        disabled={importing[product.catalog_reference]}
                        size="sm"
                        className="bg-[#8C9D78] hover:bg-[#7A8B69] rounded-full flex-shrink-0"
                        data-testid={`import-${product.catalog_reference}`}
                      >
                        {importing[product.catalog_reference] ? (
                          <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                          <>
                            <Plus className="w-4 h-4 mr-1" />
                            Importer
                          </>
                        )}
                      </Button>
                    )}
                  </div>

                  <p className="text-sm text-stone-600 line-clamp-2">{product.description}</p>

                  {product.composition && (
                    <p className="text-xs text-stone-500">Composition : {product.composition}</p>
                  )}

                  {/* Colors */}
                  {product.colors.length > 0 && (
                    <div className="flex flex-wrap gap-1.5">
                      {product.colors.map((color, i) => (
                        <span key={i} className="px-2 py-0.5 bg-stone-100 rounded-full text-xs text-stone-600">{color}</span>
                      ))}
                    </div>
                  )}

                  {/* Color images preview */}
                  {Object.keys(product.color_images).length > 0 && (
                    <div className="flex gap-1.5 flex-wrap">
                      {Object.entries(product.color_images).slice(0, 8).map(([color, url]) => (
                        <img key={color} src={url} alt={color} title={color} className="w-10 h-10 rounded-lg object-cover border border-stone-200" />
                      ))}
                      {Object.keys(product.color_images).length > 8 && (
                        <span className="w-10 h-10 rounded-lg bg-stone-100 flex items-center justify-center text-xs text-stone-400">
                          +{Object.keys(product.color_images).length - 8}
                        </span>
                      )}
                    </div>
                  )}

                  {/* Sizes */}
                  {product.sizes.length > 0 && (
                    <div className="flex flex-wrap gap-1">
                      <span className="text-xs text-stone-400 mr-1">Tailles:</span>
                      {product.sizes.map((s, i) => (
                        <span key={i} className="text-xs text-stone-500">{s}{i < product.sizes.length - 1 ? "," : ""}</span>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
};

export default TopTexCatalogPage;
